import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import AcordoRenegociacao from '../models/acordo-rengociacao.model';
import { AcordoVigente } from '../models/acordo-vigente.model';
import ContratoVendido from '../models/contrato-vencido.model';
import { Contrato } from '../models/contrato.model';
import { EnviaEmailBoletoRequest } from '../models/enviar-email-boleto-request.model';
import { EnviaSmsBoletoRequest } from '../models/enviar-sms-boleto-request.model';
import PlanoRenegociacaoMassivaCyber from '../models/plano-renegociacao-massiva-cyber.model';
import PlanoRenegociacaoMassiva from '../models/plano-renegociacao-massiva.model';
import { PlanoRenegociacao } from '../models/plano-renegociacao.model';
import PropostaOutroCanal from '../models/proposta-outro-canal.model';
import BrowserUtils from '../utils/browser-utils';
import StringUtils from '../utils/string-utils';
import { EfetivarSegundaViaResponse } from './../models/segunda-via-response.model';
import { AutenticacaoService } from 'src/app/seguranca/autenticacao.service';
import { ApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class ContratosApi {
  private readonly HASH_PORTAL_PUBLICO = '56b04662b79b0cf617bc0c32339ee1d0cf70e2d91bb70573dcea167ad3f479e3';

  constructor(
    private http: HttpClient,
    private autenticacaoService: AutenticacaoService,
    private apiService: ApiService
  ) { }

  getContratos(cpf: string): Observable<Array<Contrato>> {
    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-parcelas',
      path: `/parcelas/contratos/${cpf}/clientes`,
      oldBrowserFallbackPath: `/contratos/${cpf}/clientes`
    })

    return this.http.get<Array<Contrato>>(path, {});
  }

  getContrato(contratoId: string, cpf: string): Observable<Contrato> {
    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-contratos',
      path: `/contratos/${contratoId}/${cpf}`,
      oldBrowserFallbackPath: `/${contratoId}/${cpf}`
    })

    return this.http.get<Contrato>(path, {});
  }

  getSegundaVia(numeroContrato: string, numeroParcela: number): Observable<EfetivarSegundaViaResponse> {
    const url = `${this.getApiUrlService(true)}/cdc-cobranca/renegociacao/parcela/segunda-via/efetivar`

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca-renegociacao-service',
      path: '/cdc-cobranca/renegociacao/parcela/segunda-via/efetivar',
      oldBrowserFallbackPath: '/parcela/segunda-via/efetivar'
    })
    return this.http.post<EfetivarSegundaViaResponse>(path, { numeroContrato, numeroParcela }, {
      headers: {
        Authorization: `Bearer ${this.autenticacaoService.token}`
      }
    });
  }

  getBoleto(numeroContrato: string, numeroParcela: number): Observable<string> {
    const url = `${this.getApiUrlService()}/cobranca/parcelas/boletos/impressoes_PDF/${numeroContrato}/${numeroParcela}/${numeroParcela}`;

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: `/cobranca/parcelas/boletos/impressoes_PDF/${numeroContrato}/${numeroParcela}/${numeroParcela}`,
      oldBrowserFallbackPath: `/parcelas/boletos/impressoes_PDF/${numeroContrato}/${numeroParcela}/${numeroParcela}`
    })
    return this.http.get<any>(path).pipe(map(response => response.banco));
  }

  getPlanosDeRenegociacao(contratoId: string, tokenParceiro?: string): Observable<PlanoRenegociacao[]> {
    const url = `${this.getApiUrlService()}/contratos/portal/renegociacoes/${contratoId}`;

    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-contratos',
      path: `/contratos/portal/renegociacoes/${contratoId}`,
      oldBrowserFallbackPath: `/portal/renegociacoes/${contratoId}`
    })

    let headers = new HttpHeaders();

    if (tokenParceiro) {
      headers = headers.set('X-Partner-Token', tokenParceiro);
    }

    return this.http.get<PlanoRenegociacao[]>(path, { headers })
      .pipe(map(res => res.map((plano: PlanoRenegociacao) => ({ ...plano, id: `${plano.condicoes[0].amount}${plano.numPeriods}${StringUtils.generateUniqueId()}` }))));
  }

  downloadBoletoRenegociacao(contratoId: string): Observable<any> {
    const url = `${this.getApiUrlService()}/cobranca/contratos/${contratoId}/renegociacoes/boletos/imprimir`;

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: `/cobranca/contratos/${contratoId}/renegociacoes/boletos/imprimir`,
      oldBrowserFallbackPath: `/contratos/${contratoId}/renegociacoes/boletos/imprimir`
    })
    return this.http.get<any>(path).pipe(map(response => response.banco));
  }

  // TODO: perguntar pro charles se esse endpoint deve ser alterado
  downloadBoletoRenegociacaoMassiva(contratoId: string, isQuitacao: boolean): Observable<any> {
    const url = `${this.getApiUrlService()}/parcelas/boletos/impressoes_PDF/${contratoId}/${isQuitacao}`;

    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-parcelas',
      path: `/parcelas/boletos/impressoes_PDF/${contratoId}/${isQuitacao}`,
      oldBrowserFallbackPath: `/boletos/impressoes_PDF/${contratoId}/${isQuitacao}`
    })
    return this.http.get<any>(path).pipe(map(response => response.banco));
  }

  confirmarPlanoMassivo(plano: PlanoRenegociacaoMassiva): Observable<PlanoRenegociacaoMassivaCyber> {
    const url = `${this.getApiUrlService()}/contratos/portal/renegociacoes/propostas-massiva`;

    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-contratos',
      path: '/contratos/portal/renegociacoes/propostas-massiva',
      oldBrowserFallbackPath: '/portal/renegociacoes/propostas-massiva'
    })
    return this.http.post<PlanoRenegociacaoMassivaCyber>(path, plano);
  }

  getAcordoVigente(contratoId: string): Observable<AcordoVigente> {
    const url = `${this.getApiUrlService()}/contratos/portal/renegociacoes/proposta-vigente/${contratoId}`;

    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-contratos',
      path: `/contratos/portal/renegociacoes/proposta-vigente/${contratoId}`,
      oldBrowserFallbackPath: `/portal/renegociacoes/proposta-vigente/${contratoId}`
    })
    return this.http.get<AcordoVigente>(path);
  }

  enviarBoletoEmailRenegociacao(enviarBoletoEmailRequest: EnviaEmailBoletoRequest): Observable<any> {
    const url = `${this.getApiUrlService()}/cobranca/contratos/renegociacoes/boletos/enviar`;

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: '/cobranca/contratos/renegociacoes/boletos/enviar',
      oldBrowserFallbackPath: '/contratos/renegociacoes/boletos/enviar'
    })
    return this.http.post(path, enviarBoletoEmailRequest);
  }

  enviarBoletoSmsRenegociacao(enviaSmsBoletoRequest: EnviaSmsBoletoRequest): Observable<any> {
    const data: EnviaSmsBoletoRequest = { ...enviaSmsBoletoRequest, phone: `55${enviaSmsBoletoRequest.phone}` };
    const url = `${this.getApiUrlService()}/contratos/portal/renegociacoes/enviar/linha-digitavel`;

    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-contratos',
      path: '/contratos/portal/renegociacoes/enviar/linha-digitavel',
      oldBrowserFallbackPath: '/portal/renegociacoes/enviar/linha-digitavel'
    })
    return this.http.post(path, data);
  }

  enviarBoletoEmailSegundaVia(enviarBoletoEmailRequest: EnviaEmailBoletoRequest): Observable<any> {
    const url = `${this.getApiUrlService()}/cobranca/parcelas/enviar/boleto`;

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: '/cobranca/parcelas/enviar/boleto',
      oldBrowserFallbackPath: '/parcelas/enviar/boleto'
    })
    return this.http.post(path, enviarBoletoEmailRequest);
  }

  enviarBoletoSmsSegundaVia(enviaSmsBoletoRequest: EnviaSmsBoletoRequest): Observable<any> {
    const data: EnviaSmsBoletoRequest = { ...enviaSmsBoletoRequest, phone: `55${enviaSmsBoletoRequest.phone}` };
    const url = `${this.getApiUrlService()}/cobranca/parcelas/enviar/linha-digitavel`;

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca',
      path: '/cobranca/parcelas/enviar/linha-digitavel',
      oldBrowserFallbackPath: '/parcelas/enviar/linha-digitavel'
    })
    return this.http.post(path, data);
  }

  downloadAcordoRenegociacaoPDF(acordoRenegociacao: AcordoRenegociacao) {
    const url = `${this.getApiUrlService()}/contratos/relatorios/acordo-contrato-renegociacao`;

    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-contratos',
      path: '/contratos/relatorios/acordo-contrato-renegociacao',
      oldBrowserFallbackPath: '/relatorios/acordo-contrato-renegociacao'
    })
    return this.http.post(path, acordoRenegociacao, { responseType: 'arraybuffer' });
  }

  getPropostasOutrosCanais(idContrato: string): Observable<PropostaOutroCanal[]> {
    const url = `${this.getApiUrlService()}/contratos/portal/renegociacoes/propostas-outros-canais/${idContrato}`;

    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-contratos',
      path: `/contratos/portal/renegociacoes/propostas-outros-canais/${idContrato}`,
      oldBrowserFallbackPath: `/portal/renegociacoes/propostas-outros-canais/${idContrato}`
    })
    return this.http.get<PropostaOutroCanal[]>(url);
  }

  getContratosVendidos(): Observable<ContratoVendido[]> {
    const url = `${this.getApiUrlService()}/contratos/contratos-vendidos`;

    const path = this.apiService.getEndpoint({
      apiKey: 'vv-cred-contratos',
      path: '/contratos/contratos-vendidos',
      oldBrowserFallbackPath: '/contratos-vendidos'
    })
    return this.http.get<ContratoVendido[]>(path);
  }

  getApiUrlService(semV1: boolean = false): string {
    if (BrowserUtils.isOldFirefox()) {
      return `${environment.cdccorpApiURLService}/v1`;
    }

    return `${environment.apiURLService}${semV1 ? '' : '/v1'}`;
  }

}
